import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, Plus, Pencil, Coffee, Sparkles, ChevronDown, ChevronRight } from "lucide-react";

const EMPTY = {
  room_type: "", price: 0, inventory: 1, description: "", breakfast_included: false,
  is_sold_out: false, min_price: "",
  // AI Negotiation defaults — hotel keeps full control over these caps
  negotiation_mode: "ai_auto",
  max_discount_percent: 10,
  weekend_discount_percent: 5,
  festival_discount_percent: 0,
  group_discount_percent: 15,
  min_group_size: 10,
  auto_accept: true,
  auto_reject_below: "",
};

function NegotiationRules({ value, onChange, idPrefix = "rf" }) {
  const [open, setOpen] = useState(false);
  const enabled = (value.negotiation_mode || "ai_auto") === "ai_auto";
  return (
    <div className="border border-emerald-200 bg-emerald-50/40 rounded-sm">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-3 text-left"
        data-testid={`${idPrefix}-neg-toggle`}
      >
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-emerald-700" />
          <span className="hw-head font-bold text-slate-900 text-sm">AI Negotiation Rules</span>
          <span className={`hw-chip text-[10px] ${enabled ? "border-emerald-300 bg-emerald-100 text-emerald-800" : "border-slate-300 bg-slate-100 text-slate-600"}`}>
            {enabled ? "Auto-negotiate ON" : "Manual only"}
          </span>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
      </button>
      {open && (
        <div className="p-4 pt-0 space-y-3">
          <p className="text-[11px] text-slate-600 leading-relaxed">
            Let AI negotiate on your behalf within these caps — no manual approvals needed for offers inside your rules.
          </p>
          <div className="flex items-center gap-3">
            <Switch
              checked={enabled}
              onCheckedChange={(v) => onChange({ ...value, negotiation_mode: v ? "ai_auto" : "manual" })}
              id={`${idPrefix}-neg-mode`}
              data-testid={`${idPrefix}-neg-mode`}
            />
            <Label htmlFor={`${idPrefix}-neg-mode`} className="hw-label cursor-pointer">Enable auto-negotiation</Label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="hw-label">Max discount (%)</Label>
              <Input
                type="number" min={0} max={50}
                value={value.max_discount_percent ?? 10}
                onChange={(e) => onChange({ ...value, max_discount_percent: Number(e.target.value || 0) })}
                className="rounded-sm mt-1"
                data-testid={`${idPrefix}-max-discount`}
              />
            </div>
            <div>
              <Label className="hw-label">Extra on weekends (%)</Label>
              <Input
                type="number" min={0} max={30}
                value={value.weekend_discount_percent ?? 5}
                onChange={(e) => onChange({ ...value, weekend_discount_percent: Number(e.target.value || 0) })}
                className="rounded-sm mt-1"
                data-testid={`${idPrefix}-weekend-discount`}
              />
            </div>
            <div>
              <Label className="hw-label">Festival extra (%)</Label>
              <Input
                type="number" min={0} max={30}
                value={value.festival_discount_percent ?? 0}
                onChange={(e) => onChange({ ...value, festival_discount_percent: Number(e.target.value || 0) })}
                className="rounded-sm mt-1"
                data-testid={`${idPrefix}-festival-discount`}
              />
            </div>
            <div>
              <Label className="hw-label">Group discount cap (%)</Label>
              <Input
                type="number" min={0} max={50}
                value={value.group_discount_percent ?? 15}
                onChange={(e) => onChange({ ...value, group_discount_percent: Number(e.target.value || 0) })}
                className="rounded-sm mt-1"
                data-testid={`${idPrefix}-group-discount`}
              />
            </div>
            <div>
              <Label className="hw-label">Group starts at (rooms)</Label>
              <Input
                type="number" min={2} max={100}
                value={value.min_group_size ?? 10}
                onChange={(e) => onChange({ ...value, min_group_size: Number(e.target.value || 0) })}
                className="rounded-sm mt-1"
                data-testid={`${idPrefix}-min-group-size`}
              />
            </div>
            <div>
              <Label className="hw-label">Hard reject below ₹</Label>
              <Input
                type="number" min={0}
                value={value.auto_reject_below ?? ""}
                onChange={(e) => onChange({ ...value, auto_reject_below: e.target.value })}
                className="rounded-sm mt-1"
                placeholder="Defaults to AI min"
                data-testid={`${idPrefix}-auto-reject`}
              />
            </div>
          </div>
          <div className="flex items-center gap-3 pt-1">
            <Switch
              checked={!!value.auto_accept}
              onCheckedChange={(v) => onChange({ ...value, auto_accept: v })}
              id={`${idPrefix}-auto-accept`}
              data-testid={`${idPrefix}-auto-accept`}
            />
            <Label htmlFor={`${idPrefix}-auto-accept`} className="hw-label cursor-pointer">
              Auto-accept offers within policy (else AI just counters, you approve)
            </Label>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PartnerRooms() {
  const [rooms, setRooms] = useState([]);
  const [hotel, setHotel] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/my/hotel").then(({ data }) => {
    if (data) { setHotel(data.hotel); setRooms(data.rooms || []); }
  });
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.room_type || !form.price) return toast.error("Type & price required");
    try {
      await api.post("/my/rooms", form);
      toast.success("Room added");
      setForm(EMPTY);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this room?")) return;
    await api.delete(`/my/rooms/${id}`);
    load();
  };

  const startEdit = (r) => { setEditing({ ...r }); setEditOpen(true); };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await api.put(`/my/rooms/${editing.room_id}`, editing);
      toast.success("Saved");
      setEditOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleSoldOut = async (r) => {
    try {
      await api.put(`/my/rooms/${r.room_id}`, { is_sold_out: !r.is_sold_out });
      load();
    } catch (e) { toast.error("Failed"); }
  };

  if (!hotel) return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)] grid place-items-center">
      <div className="hw-card p-10 text-center"><p className="text-slate-600">Register your hotel first.</p></div>
    </div>
  );

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)]">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        <div className="hw-label text-blue-700">Rooms</div>
        <h1 className="hw-head text-4xl md:text-5xl font-black text-slate-900 mt-3">Manage rooms</h1>
        <p className="text-slate-600 mt-2">Listed at <span className="font-semibold">{hotel.name}</span>, {hotel.city}.</p>

        <form onSubmit={add} className="hw-card p-6 mt-8 space-y-4" data-testid="room-form">
          <div className="hw-label text-slate-500">Add new room category</div>
          <div className="grid md:grid-cols-5 gap-3 items-end">
            <div className="md:col-span-2"><Label className="hw-label">Room type *</Label><Input data-testid="rf-type" value={form.room_type} onChange={(e) => setForm({ ...form, room_type: e.target.value })} placeholder="Deluxe Double" className="rounded-sm mt-1" /></div>
            <div><Label className="hw-label">Price (₹) *</Label><Input data-testid="rf-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: Number(e.target.value) })} className="rounded-sm mt-1" /></div>
            <div><Label className="hw-label">Inventory</Label><Input data-testid="rf-inv" type="number" min={0} value={form.inventory} onChange={(e) => setForm({ ...form, inventory: Number(e.target.value) })} className="rounded-sm mt-1" /></div>
            <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-slate-900 rounded-sm font-bold h-10" data-testid="rf-add"><Plus className="w-4 h-4 mr-1" /> Add</Button>
          </div>
          <div className="flex items-center gap-3 pt-2">
            <Switch checked={form.breakfast_included} onCheckedChange={(v) => setForm({ ...form, breakfast_included: v })} id="bf" data-testid="rf-breakfast" />
            <Label htmlFor="bf" className="hw-label cursor-pointer">Breakfast complimentary</Label>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="hw-label">Description (optional)</Label>
              <Input data-testid="rf-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="King bed, 28 sqm, city view" className="rounded-sm mt-1" />
            </div>
            <div>
              <Label className="hw-label flex items-center gap-1">AI min price (₹/night) <span className="text-[10px] text-emerald-700 font-bold">floor · never sold below</span></Label>
              <Input data-testid="rf-min-price" type="number" min={0} value={form.min_price} onChange={(e) => setForm({ ...form, min_price: e.target.value })} placeholder="e.g. 1500" className="rounded-sm mt-1" />
            </div>
          </div>
          <NegotiationRules value={form} onChange={setForm} idPrefix="rf" />
        </form>

        <div className="space-y-3 mt-8" data-testid="rooms-list">
          {rooms.length === 0 ? <div className="hw-card p-10 text-center text-slate-600">No rooms yet.</div> : rooms.map((r) => {
            const effectivelySoldOut = r.is_sold_out || (r.inventory || 0) <= 0;
            const negOn = (r.negotiation_mode || "ai_auto") === "ai_auto";
            return (
              <div key={r.room_id} className="hw-card p-5 flex items-center justify-between flex-wrap gap-3" data-testid={`room-${r.room_id}`}>
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="hw-head font-bold text-lg text-slate-900">{r.room_type}</h3>
                    {r.breakfast_included && <span className="hw-chip border-emerald-300 bg-emerald-50 text-emerald-700"><Coffee className="w-3 h-3" /> Breakfast</span>}
                    {effectivelySoldOut && <span className="hw-chip border-red-300 bg-red-50 text-red-700">Sold out</span>}
                    <span className={`hw-chip text-[10px] ${negOn ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-300 bg-slate-50 text-slate-600"}`} data-testid={`neg-badge-${r.room_id}`}>
                      <Sparkles className="w-3 h-3" /> {negOn ? "AI auto-negotiate" : "Manual only"}
                    </span>
                  </div>
                  <div className="text-sm text-slate-500 mt-1">{r.inventory || 0} units · {r.description || "—"}</div>
                  <div className="flex flex-wrap gap-3 mt-1">
                    {r.min_price && (
                      <div className="text-[10px] font-bold uppercase text-emerald-700">AI floor ₹{Number(r.min_price).toLocaleString("en-IN")}</div>
                    )}
                    {negOn && (
                      <div className="text-[10px] font-bold uppercase text-slate-500">
                        Max −{r.max_discount_percent ?? 10}% · Wknd +{r.weekend_discount_percent ?? 5}% · Group ≥{r.min_group_size ?? 10} rooms
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="hw-head font-black text-2xl text-slate-900">₹{r.price?.toLocaleString("en-IN")}</div>
                  <div className="flex items-center gap-2">
                    <Switch checked={r.is_sold_out} onCheckedChange={() => toggleSoldOut(r)} data-testid={`sold-${r.room_id}`} />
                    <span className="hw-label text-slate-500">Sold out</span>
                  </div>
                  <Button onClick={() => startEdit(r)} variant="outline" size="sm" className="rounded-sm" data-testid={`edit-${r.room_id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button onClick={() => del(r.room_id)} variant="outline" size="sm" className="rounded-sm border-red-300 text-red-700" data-testid={`del-${r.room_id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-sm max-h-[90vh] overflow-y-auto" data-testid="edit-room-dialog">
          <DialogHeader><DialogTitle className="hw-head font-black">Edit room</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label className="hw-label">Room type</Label><Input value={editing.room_type} onChange={(e) => setEditing({ ...editing, room_type: e.target.value })} className="rounded-sm mt-1" data-testid="edit-type" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="hw-label">Price (₹)</Label><Input type="number" value={editing.price} onChange={(e) => setEditing({ ...editing, price: Number(e.target.value) })} className="rounded-sm mt-1" data-testid="edit-price" /></div>
                <div><Label className="hw-label">Inventory</Label><Input type="number" min={0} value={editing.inventory} onChange={(e) => setEditing({ ...editing, inventory: Number(e.target.value) })} className="rounded-sm mt-1" data-testid="edit-inventory" /></div>
              </div>
              <div><Label className="hw-label">Description</Label><Input value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="rounded-sm mt-1" data-testid="edit-desc" /></div>
              <div>
                <Label className="hw-label">AI min price (₹/night) <span className="text-[10px] text-emerald-700 font-bold">Auto-Bidding floor</span></Label>
                <Input type="number" min={0} value={editing.min_price ?? ""} onChange={(e) => setEditing({ ...editing, min_price: e.target.value })} className="rounded-sm mt-1" data-testid="edit-min-price" placeholder="e.g. 1500" />
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={!!editing.breakfast_included} onCheckedChange={(v) => setEditing({ ...editing, breakfast_included: v })} id="ed-bf" data-testid="edit-breakfast" />
                <Label htmlFor="ed-bf" className="hw-label">Breakfast complimentary</Label>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={!!editing.is_sold_out} onCheckedChange={(v) => setEditing({ ...editing, is_sold_out: v })} id="ed-so" data-testid="edit-soldout" />
                <Label htmlFor="ed-so" className="hw-label">Sold out</Label>
              </div>
              <NegotiationRules value={editing} onChange={setEditing} idPrefix="edit" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={saveEdit} className="bg-emerald-600 hover:bg-emerald-700 text-slate-900 rounded-sm font-bold" data-testid="edit-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
